import React, { useCallback, useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useLazyQuery } from '@apollo/client';

import {
  GoogleMap,
  Marker,
  useLoadScript,
  MarkerClusterer,
  StandaloneSearchBox,
  InfoWindow,
} from '@react-google-maps/api';
import { gqlGet, gqlGetAggr } from './graphql';
import Spiderfy from './spiderfy';
import { FlexRow } from 'motion-components';
import { isAfisCyprus, isMedicycle } from 'shared-pages/common/constants';
import { useTranslation } from 'react-i18next';
import { isArray } from 'lodash';

const GREECE_BOUNDS = {
  north: 44.108926,
  south: 33.957559,
  west: 18.17496,
  east: 32.061679,
};

// const CYPRUS_BOUNDS = {
//   north: 35.650118,
//   south: 34.504542,
//   west: 32.102231,
//   east: 34.725597,
// };
const CYPRUS_BOUNDS = {
  north: 35.47392404583963,
  south: 34.57751314466643,
  west: 32.28362020964394,
  east: 34.15140691245629,
};

const isCyprus = isAfisCyprus || isMedicycle;
const MAP_BOUNDS = isCyprus ? CYPRUS_BOUNDS : GREECE_BOUNDS;

const STARTING_ZOOM = isCyprus ? 10 : 17;

/* ---------------- Page ------------------ */
export const CollectionpointsMapPage = () => {
  const [userCoords, setUserCoords] = useState({ lat: 38.02, lng: 23.805 });
  const { t } = useTranslation();

  useEffect(() => {
    if ('geolocation' in navigator) {
      console.log('Available');
      if (isCyprus) {
        setUserCoords({ lat: 34.695, lng: 33.017 });
      } else {
        navigator.geolocation.getCurrentPosition(function (position) {
          console.log('Latitude is :', position.coords.latitude);
          console.log('Longitude is :', position.coords.longitude);
          setUserCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
        });
      }
    } else {
      console.log('Not Available');
    }
  }, []);
  return (
    <Card className="shadow">
      <Card.Header>
        <FlexRow>
          <Card.Title className="m-0">{t('Χάρτης Σημείων Συλλογής')}</Card.Title>
        </FlexRow>
      </Card.Header>
      <Card.Body className="nopadding" style={{ minHeight: '350px' }}>
        {/* <div className="m-5">Τοποθεσία Χρήστη = {JSON.stringify(userCoords)}</div> */}
        <Map center={userCoords} />
      </Card.Body>
    </Card>
  );
};

/* ---------------- Map ------------------ */
const containerStyle = {
  width: '100%',
  height: '80vh',
};
const libraries = ['places'];
export const Map = ({ center: initCenter, initZoom = STARTING_ZOOM }) => {
  const { t } = useTranslation();

  console.log('process.env.VITE_APP_GOOGLEMAPKEY', process.env.VITE_APP_GOOGLEMAPKEY);
  const { isLoaded, loadError } = useLoadScript({
    version: 'beta',
    //language: 'greek',
    googleMapsApiKey: process.env.VITE_APP_GOOGLEMAPKEY,
    // ...otherOptions
    libraries: libraries as any,
  });
  const [map, setMap] = React.useState(null);

  const [center, setCenter] = useState(initCenter);
  const onCenterChanged = useCallback(() => {
    if (map) {
      const c = map.getCenter().toJSON();
      setCenter(c);
    }
  }, [map]);

  const [zoom, setZoom] = useState(initZoom);
  const onZoomChanged = useCallback(() => {
    if (map) setZoom(map.getZoom());
  }, [map]);

  const [bounds, setBounds] = useState<any>();
  let boundsDelayChange;
  const onBoundsChanged = useCallback(() => {
    if (map) {
      const b = map.getBounds();
      const ne = b.getNorthEast();
      const sw = b.getSouthWest();
      const bounds = {
        fromLat: sw.lat(),
        tillLat: ne.lat(),
        fromLng: sw.lng(),
        tillLng: ne.lng(),
      };
      console.log('bounds changed = ', bounds, b);
      clearTimeout(boundsDelayChange);
      boundsDelayChange = setTimeout(() => setBounds(bounds), 500);
    }
  }, [map]);

  /* ---------- Place Search ----------------- */
  const [placeMarker, setPlaceMarker] = useState(null);
  const clearPreviousMarker = useCallback(() => {
    if (placeMarker) {
      placeMarker.setMap(null);
      setPlaceMarker(null);
    }
  }, [placeMarker]);
  const [searchBox, setSearchBox] = useState(null);
  const onLoad = (ref) => setSearchBox(ref);
  const onPlacesChanged = () => {
    // Clear Prv Marker
    clearPreviousMarker();

    const places = searchBox.getPlaces();

    if (!places || places.length === 0) return;
    const name = places[0].name;
    const loc = places[0].geometry.location;
    map.setCenter(loc);
    map.setZoom(19);

    // Add Marker
    const mrk = new google.maps.Marker({
      map: map,
      label: name,
      title: name,
      position: loc,
    });
    setPlaceMarker(mrk);
  };

  const onMarkerClick = (latLng) => {
    if (zoom > 17) return;
    map.setCenter(latLng);
    if (zoom > 15) map.setZoom(zoom + 1);
    else if (zoom < 21) map.setZoom(zoom + 2);
  };

  // Render --------------------------------------
  if (loadError) {
    return <div>{t('Εμφανίστηκε πρόβλημα.')}</div>;
  } else if (!isLoaded) return <div>{t('Φόρτωση...')}</div>;
  else
    return (
      <>
        <GoogleMap
          onLoad={(map) => {
            console.log('map loaded', map);
            setMap(map);
          }}
          mapContainerStyle={containerStyle}
          center={initCenter}
          onCenterChanged={onCenterChanged}
          zoom={initZoom}
          onZoomChanged={onZoomChanged}
          onBoundsChanged={onBoundsChanged}
          options={{
            restriction: { latLngBounds: MAP_BOUNDS, strictBounds: false },
            minZoom: 6,
          }}
        >
          <StandaloneSearchBox
            onLoad={onLoad}
            onPlacesChanged={onPlacesChanged}
            bounds={MAP_BOUNDS}
          >
            <input
              type="search"
              placeholder="Εύρεση διεύθυνσης"
              style={{
                boxSizing: `border-box`,
                border: `1px solid transparent`,
                height: `32px`,
                padding: `0 12px`,
                borderRadius: `3px`,
                boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`,
                fontSize: `14px`,
                outline: `none`,
                textOverflow: `ellipses`,
                position: 'absolute',
                top: '12px',
                left: '42%',
                width: `35%`,
                marginLeft: '-120px',
              }}
              onChange={(el) => {
                if (!el.currentTarget.value) clearPreviousMarker();
              }}
            />
          </StandaloneSearchBox>

          <CollectionPointsMarkers zoom={zoom} bounds={bounds} onMarkerClick={onMarkerClick} />
        </GoogleMap>
        <div className="p-2" style={{ color: 'dimgray' }}>
          zoom: {zoom}, center: (lat {center.lat}, lng {center.lng})
        </div>
      </>
    );
};

/* ---------------- Markers ------------------ */
export const CollectionPointsMarkers = ({ bounds, zoom, onMarkerClick }) => {
  const [activeZoom, setActiveZoom] = React.useState(zoom);

  // Debounce zoom updates so transitions happen cleanly after map animation settles
  useEffect(() => {
    const handler = setTimeout(() => setActiveZoom(zoom), 300);
    return () => clearTimeout(handler);
  }, [zoom]);

  // Unique key per map layer mode (forces unmount/remount when switching)
  const mode = !isCyprus
    ? activeZoom <= 13
      ? 'gr-nomos'
      : activeZoom < 16
      ? 'gr-postal'
      : 'detail'
    : activeZoom <= 13
    ? 'cy-region'
    : activeZoom < 16
    ? 'cy-city'
    : 'detail';

  if (!isCyprus) {
    // Greece mode
    if (activeZoom <= 13) {
      return (
        <React.Fragment key={mode}>
          <CollectionPointsMarkersAggr
            bounds={bounds}
            zoom={activeZoom}
            grouping="Nomos"
            onMarkerClick={onMarkerClick}
            clusterKey={mode}
          />
        </React.Fragment>
      );
    } else if (activeZoom < 16) {
      return (
        <React.Fragment key={mode}>
          <CollectionPointsMarkersAggr
            bounds={bounds}
            zoom={activeZoom}
            grouping="PostalCode"
            onMarkerClick={onMarkerClick}
            clusterKey={mode}
          />
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment key={mode}>
          <CollectionPointsMarkersDetail
            bounds={bounds}
            zoom={activeZoom}
            onMarkerClick={onMarkerClick}
            clusterKey={mode}
          />
        </React.Fragment>
      );
    }
  } else {
    // Cyprus mode
    if (activeZoom <= 13) {
      return (
        <React.Fragment key={mode}>
          <CollectionPointsMarkersAggr
            bounds={bounds}
            zoom={activeZoom}
            grouping="Region"
            onMarkerClick={onMarkerClick}
            clusterKey={mode}
          />
        </React.Fragment>
      );
    } else if (activeZoom < 16) {
      return (
        <React.Fragment key={mode}>
          <CollectionPointsMarkersAggr
            bounds={bounds}
            zoom={activeZoom}
            grouping="City"
            onMarkerClick={onMarkerClick}
            clusterKey={mode}
          />
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment key={mode}>
          <CollectionPointsMarkersDetail
            bounds={bounds}
            zoom={activeZoom}
            onMarkerClick={onMarkerClick}
            clusterKey={mode}
          />
        </React.Fragment>
      );
    }
  }
};

/* ---------------- Markers Detail ------------------ */
const SPIDERFY = true;
export const CollectionPointsMarkersDetail = ({ bounds, zoom, onMarkerClick, clusterKey }) => {
  useEffect(() => {
    console.log('CollectionPointsMarkersDetail bounds', bounds);
  }, [bounds]);
  //   return null;

  //const { markers } = useFetchDemoData(bounds);
  const { markers } = useFetchData(bounds);

  const [activeMarker, setActiveMarker] = useState(null);
  const handleActiveMarker = (marker) => {
    if (marker === activeMarker) {
      return;
    }
    setActiveMarker(marker);
  };

  const constructMarkers = (clusterer) => {
    const googleMarkers = markers.map((cp) => {
      // console.log('$$$ marker', cp);
      return (
        <Marker
          key={cp.id}
          // label={zoom > 17 ? cp.label : ''}
          // options={{
          //   collisionBehavior: 'OPTIONAL_AND_HIDES_LOWER_PRIORITY',
          //   //google.maps.CollisionBehavior.REQUIRED_AND_HIDES_OPTIONAL,
          // }}
          title={cp.title}
          position={cp.position}
          icon={{
            //url: './favicon.png',
            url: './map-marker.png',
            scaledSize: new google.maps.Size(36, 36),
            //origin: new google.maps.Point(8, 12),
            labelOrigin: new google.maps.Point(16, 48), // το κείμενο απο κάτω
          }}
          label={
            zoom >= 18
              ? {
                  text: cp.label,
                  fontSize: zoom > 19 ? '16px' : '14px',
                  fontFamily: '"Courier New", Courier,Monospace',
                  color: 'green',
                }
              : null
          }
          clusterer={clusterer}
          onClick={(e) => {
            onMarkerClick(e.latLng); // Zoom
            handleActiveMarker(cp.id); // Show Info Window
          }}
        >
          {activeMarker === cp.id ? (
            /**
             * Εμφανίζεται οταν κάνεις click στο σημείο
             */
            <InfoWindow onCloseClick={() => setActiveMarker(null)}>
              <div className="poi-info-window">
                <div className="title">
                  <span style={{ fontSize: 'larger' }}>{cp.collectionpointName}</span>{' '}
                  <small style={{ color: 'gray' }}>({cp.id})</small>
                </div>
                {cp.addressDescription &&
                  cp.addressDescription !==
                    cp.collectionpointAddress.substring(
                      0,
                      cp.collectionpointAddress.indexOf(','),
                    ) && <div style={{ color: 'gray' }}>{cp.addressDescription}</div>}
                {cp.collectionpointProfession && (
                  <div style={{ color: 'gray', fontWeight: '600' }}>
                    {cp.collectionpointProfession}
                  </div>
                )}
                {cp.collectionpointAddress && (
                  <div className="address-line">{cp.collectionpointAddress}</div>
                )}
              </div>
            </InfoWindow>
          ) : null}
        </Marker>
      );
    });
    //console.log('return googleMarkers', googleMarkers[0]);
    return googleMarkers;
  };

  if (!markers) return null;
  else if (SPIDERFY)
    return (
      <MarkerClusterer key={clusterKey} averageCenter enableRetinaIcons gridSize={60} maxZoom={17}>
        {(clusterer) => {
          return <Spiderfy>{constructMarkers(clusterer)}</Spiderfy>;
        }}
      </MarkerClusterer>
    );
  else
    return (
      <MarkerClusterer key={clusterKey} averageCenter enableRetinaIcons gridSize={60} maxZoom={17}>
        {(clusterer) => (
          <>
            {markers.map((cp) => (
              <Marker
                key={cp.id}
                label={zoom > 16 ? cp.label : cp.label}
                title={cp.title}
                position={cp.position}
                icon={{
                  // url: './favicon.png',
                  url: './logo-32x32.png',
                  scaledSize: new google.maps.Size(32, 32),
                }}
                clusterer={clusterer}
                onClick={(e) => {
                  onMarkerClick(e.latLng);
                }}
              />
            ))}
          </>
        )}
      </MarkerClusterer>
    );
};

/* ------------------ Fetch Data ---------------------------- */
interface LatLngLiteral {
  lat: number;
  lng: number;
}
interface MarkerData {
  id: number;
  label: string;
  title: string;
  position: LatLngLiteral;
  collectionpointName?: string;
  collectionpointProfession?: string;
  collectionpointAddress?: string;
  addressDescription?: string;
}

function useFetchDemoData(bounds) {
  // Markers
  const [markers, setMarkers] = useState<Array<MarkerData>>();
  useEffect(() => {
    const points = [
      {
        id: 1,
        label: 'Κατάστημα 1',
        title: `Motion 1`,
        position: {
          lat: 38.02377,
          lng: 23.80597,
        },
      },
      {
        id: 2,
        label: 'Γραφείο 2',
        title: `Motion 2`,
        position: {
          lat: 38.02377,
          lng: 23.80597,
        },
      },
      {
        id: 3,
        label: 'Ιατρείο 3',
        title: `Motion 3`,
        position: {
          lat: 38.023775,
          lng: 23.805975,
        },
      },

      {
        id: 4,
        label: 'Γυμναστήριο 4',
        title: `Motion 4`,
        position: {
          lat: 38.02377,
          lng: 23.80617,
        },
      },
    ];
    setMarkers(points);
  }, []);
  return { markers };
}
function useFetchData(bounds) {
  // Markers
  const [markers, setMarkers] = useState<Array<MarkerData>>();

  // Load Data
  const [get] = useLazyQuery(gqlGet, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const newMarkers = data.getCollectionpoints?.data.map((rec) => {
        let label = `${rec.name}`;
        if (label.length > 10) label = label.substring(0, 7) + '...';
        /** title: Εμφανίζεται σαν onHint */
        const title = `${rec.name}${rec.profession?.name ? `\n${rec.profession?.name}` : ''}\n${
          rec.addressLine1
        }${rec.postalCode ? ', ' + rec.postalCode : ''}${
          rec.addressCity ? ', ' + rec.addressCity : ''
        }`;

        return {
          id: rec.id,
          label: label,
          title: title,
          collectionpointName: rec.name,
          collectionpointProfession: rec.profession?.name ?? '',
          collectionpointAddress: `${rec.addressLine1}${
            rec.postalCode ? ', ' + rec.postalCode : ''
          }${rec.addressCity ? ', ' + rec.addressCity : ''}`,
          addressDescription: rec.addressDescription,
          position: {
            lat: parseFloat(rec.latitude),
            lng: parseFloat(rec.longitude),
          },
        };
      });
      setMarkers(newMarkers);
    },
  });

  useEffect(() => {
    if (!bounds || !bounds.fromLat) return;
    const where = {
      $and: [
        { latitude: { $gte: bounds.fromLat } },
        { latitude: { $lte: bounds.tillLat } },
        { longitude: { $gte: bounds.fromLng } },
        { longitude: { $lte: bounds.tillLng } },
      ],
    };
    // const where = {
    //   $and: [{ latitude: { $gt: 0 } }],
    // };
    get({
      variables: {
        where: JSON.stringify(where),
        limit: 1000,
      },
    });
  }, [bounds, get]);

  return { markers };
}

/* ---------------- Markers Aggr Detail ------------------ */
export const CollectionPointsMarkersAggr = ({
  bounds,
  zoom,
  grouping,
  onMarkerClick,
  clusterKey,
}) => {
  useEffect(() => {
    console.log('CollectionPointsMarkersAggr bounds', bounds);
  }, [bounds]);
  //   return null;

  const { markers } = useFetchDataAggr(bounds, grouping);

  if (!markers) return null;
  else
    return (
      <>
        {markers.map((cp) => {
          return (
            <Marker
              key={cp.id}
              label={cp.label}
              title={cp.title}
              position={cp.position}
              icon={{
                // url: '/favicon.png',
                url: './map-marker.png',
                scaledSize: new google.maps.Size(36, 36),
              }}
              onClick={(e) => {
                onMarkerClick(e.latLng);
              }}
            />
          );
        })}
      </>
    );
};
/* ------------------ Fetch Aggr Data ---------------------------- */
function useFetchDataAggr(bounds, grouping) {
  // Markers
  const [markers, setMarkers] = useState<Array<MarkerData>>();

  // Load Data
  const [get] = useLazyQuery(gqlGetAggr, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (
        data &&
        data.getCollectionpointGeocodingAggr &&
        isArray(data.getCollectionpointGeocodingAggr)
      ) {
        const newMarkers = data.getCollectionpointGeocodingAggr?.map((rec) => {
          return {
            id: rec.name,
            label: `${rec.value}`,
            title: `${grouping === 'PostalCode' ? 'Τ.Κ. ' : ''}${rec.name}\n${rec.value} ${
              rec.value === 1 ? 'σημείο' : 'σημεία'
            }`,
            position: {
              lat: parseFloat(rec.latitude),
              lng: parseFloat(rec.longitude),
            },
          };
        });
        setMarkers(newMarkers);
        console.log(`useFetchData Aggr grouping=${grouping} len=${newMarkers.length}`);
      } else {
        console.log('useFetchData Aggr onCompleted data empty?', grouping, data);
      }
    },
  });

  useEffect(() => {
    if (!bounds || !bounds.fromLat) return;
    get({
      variables: {
        grouping: grouping,
        fromLat: bounds.fromLat,
        tillLat: bounds.tillLat,
        fromLon: bounds.fromLng,
        tillLon: bounds.tillLng,
      },
    });
  }, [bounds, get, grouping]);

  return { markers };
}
